import PropTypes from "prop-types";
import { useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import {
  FUEL_TYPE_OPTIONS,
  DUTY_RATE_BODY_TYPE_OPTIONS,
  INPUT_SX,
  GREEN_BUTTON_SX,
  formatMoney,
} from "layouts/dutyEstimator/constants";

const EMPTY_FORM = {
  categoryId: "",
  condition: "new",
  cifValueUSD: "",
  fuelType: "",
  bodyType: "",
  engineCC: "",
  hsCode: "",
  modelYear: "",
  cifExcludesDepreciation: false,
  ghsDisinfectionFeeGHS: "",
};

const SummaryRow = ({ label, value, bold }) => (
  <TableRow>
    <TableCell sx={{ borderBottom: "1px solid #f0f0f0", py: 1 }}>
      <MDTypography variant={bold ? "button" : "caption"} fontWeight={bold ? "bold" : "regular"}>
        {label}
      </MDTypography>
    </TableCell>
    <TableCell align="right" sx={{ borderBottom: "1px solid #f0f0f0", py: 1 }}>
      <MDTypography variant={bold ? "button" : "caption"} fontWeight={bold ? "bold" : "medium"}>
        {value}
      </MDTypography>
    </TableCell>
  </TableRow>
);

SummaryRow.defaultProps = { bold: false };
SummaryRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  bold: PropTypes.bool,
};

function PreviewPanel({ categoryOptions, onPreview, onValidationError }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);

  const isUsed = form.condition === "used";

  const handleRun = async () => {
    if (!form.categoryId || form.cifValueUSD === "") {
      onValidationError("Please choose a category and enter a CIF value.");
      return;
    }
    if (isUsed && form.modelYear === "") {
      onValidationError("A used vehicle needs a model year so the age bands can be applied.");
      return;
    }

    const payload = {
      categoryId: Number(form.categoryId),
      condition: form.condition,
      cifValueUSD: Number(form.cifValueUSD),
    };

    if (form.fuelType) payload.fuelType = form.fuelType;
    if (form.bodyType) payload.bodyType = form.bodyType;
    if (form.engineCC !== "") payload.engineCC = Number(form.engineCC);
    if (form.hsCode) payload.hsCode = form.hsCode;
    if (form.ghsDisinfectionFeeGHS !== "") payload.ghsDisinfectionFeeGHS = Number(form.ghsDisinfectionFeeGHS);
    if (isUsed && form.modelYear !== "") payload.modelYear = Number(form.modelYear);
    if (isUsed && form.cifExcludesDepreciation) payload.cifExcludesDepreciation = true;

    setResult((await onPreview(payload)) || null);
  };

  const field = (props) => (
    <Grid item xs={12} sm={6}>
      <TextField fullWidth sx={INPUT_SX} {...props} />
    </Grid>
  );

  return (
    <PanelCard
      title="Test an Estimate"
      caption="Runs against the saved settings without changing anything. Use it to check a rate change before customers see it."
    >
      <MDBox pt={3} pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              {field({
                select: true,
                label: "Category",
                value: form.categoryId,
                onChange: (e) => setForm({ ...form, categoryId: e.target.value }),
                SelectProps: { IconComponent: ArrowDropDownIcon },
                children:
                  categoryOptions.length === 0
                    ? [
                        <MenuItem disabled value="" key="none">
                          No categories available
                        </MenuItem>,
                      ]
                    : categoryOptions.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </MenuItem>
                      )),
              })}

              {field({
                select: true,
                label: "Condition",
                value: form.condition,
                onChange: (e) => setForm({ ...form, condition: e.target.value }),
                SelectProps: { IconComponent: ArrowDropDownIcon },
                children: [
                  <MenuItem value="new" key="new">
                    New
                  </MenuItem>,
                  <MenuItem value="used" key="used">
                    Used
                  </MenuItem>,
                ],
              })}

              {field({
                type: "number",
                label: "CIF Value (USD)",
                value: form.cifValueUSD,
                onChange: (e) => setForm({ ...form, cifValueUSD: e.target.value }),
                helperText: isUsed
                  ? form.cifExcludesDepreciation
                    ? "Pre-depreciation value — the age discount below will be applied to it"
                    : "The final, landed CIF — used as-is, no further discount"
                  : "Cost, Insurance & Freight, entered directly",
              })}

              {isUsed &&
                field({
                  type: "number",
                  label: "Model Year",
                  value: form.modelYear,
                  onChange: (e) => setForm({ ...form, modelYear: e.target.value }),
                  helperText: "Drives depreciation and the penalty band",
                })}

              {field({
                select: true,
                label: "Fuel Type",
                value: form.fuelType,
                onChange: (e) => setForm({ ...form, fuelType: e.target.value }),
                SelectProps: { IconComponent: ArrowDropDownIcon },
                children: [
                  <MenuItem value="" key="none">
                    Not specified
                  </MenuItem>,
                  ...FUEL_TYPE_OPTIONS.filter((o) => o.value !== "any").map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                ],
              })}

              {field({
                select: true,
                label: "Body Type",
                value: form.bodyType,
                onChange: (e) => setForm({ ...form, bodyType: e.target.value }),
                SelectProps: { IconComponent: ArrowDropDownIcon },
                helperText: "Picks a body-type-scoped duty rate within the category, if one exists",
                children: [
                  <MenuItem value="" key="none">
                    Not specified
                  </MenuItem>,
                  ...DUTY_RATE_BODY_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                ],
              })}

              {field({
                type: "number",
                label: "Engine (cc)",
                value: form.engineCC,
                onChange: (e) => setForm({ ...form, engineCC: e.target.value }),
              })}

              {field({
                label: "HS Code",
                value: form.hsCode,
                onChange: (e) => setForm({ ...form, hsCode: e.target.value }),
                helperText: "Optional — picks the rate directly",
              })}

              {field({
                type: "number",
                label: "Disinfection Fee (GHS)",
                value: form.ghsDisinfectionFeeGHS,
                onChange: (e) => setForm({ ...form, ghsDisinfectionFeeGHS: e.target.value }),
                helperText: "Blank uses the admin default",
              })}

              {isUsed ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.cifExcludesDepreciation}
                        onChange={(e) => setForm({ ...form, cifExcludesDepreciation: e.target.checked })}
                      />
                    }
                    label={
                      <MDTypography variant="button" fontWeight="regular">
                        This is a pre-depreciation value, not a final CIF
                      </MDTypography>
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    Off by default (2026-08-24) — a used vehicle&apos;s CIF is now treated as the
                    final, landed value and is not discounted again. Turn this on only if you&apos;re
                    testing with a manufacturer&apos;s pre-depreciation benchmark/HDV price instead
                    of a real CIF.
                  </MDTypography>
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Button fullWidth variant="contained" onClick={handleRun} sx={GREEN_BUTTON_SX}>
                  Calculate Estimate
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={7}>
            {!result ? (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight={240}
                borderRadius="lg"
                sx={{ border: "1px dashed #d0d0d0" }}
              >
                <MDTypography variant="button" color="text">
                  Fill in the vehicle details and calculate to see the full breakdown.
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium">
                  Valuation
                </MDTypography>
                <Table size="small">
                  <TableBody>
                    <SummaryRow label="Exchange rate" value={`1 USD = ${result.valuation?.exchangeRateUsdToGhs} GHS`} />
                    {result.valuation?.depreciation?.applied ? (
                      <SummaryRow
                        label={`Depreciation — ${result.valuation.depreciation.band || "no band"}`}
                        value={`${result.valuation.depreciation.rate}%  (−$${formatMoney(result.valuation.depreciation.amountUSD)})`}
                      />
                    ) : null}
                    <SummaryRow label="CIF Value (GHS)" value={formatMoney(result.input?.cifValueUSD)} />
                    <SummaryRow label="Dutiable CIF (GHS)" value={formatMoney(result.valuation?.dutiableCifUSD)} />
                    <SummaryRow label="Customs Value (GHS)" value={formatMoney(result.valuation?.customsValueGHS)} bold />
                    <SummaryRow
                      label="Import VAT base — Customs Value + Duty (GHS)"
                      value={formatMoney(result.valuation?.importVatBaseGHS)}
                    />
                  </TableBody>
                </Table>

                <MDBox mt={1} mb={2} display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`Duty ${result.dutyRate?.rate}%${result.dutyRate?.hsCode ? ` · HS ${result.dutyRate.hsCode}` : ""}`}
                    sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                  />
                  {result.input?.bodyType ? (
                    <Chip
                      size="small"
                      label={`Body type: ${result.input.bodyType}`}
                      sx={{ backgroundColor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }}
                    />
                  ) : null}
                  {result.overAgePenalty?.applied ? (
                    <Chip
                      size="small"
                      label={`Over-age ${result.overAgePenalty.rate}% · ${result.overAgePenalty.band}`}
                      sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
                    />
                  ) : null}
                </MDBox>

                <Divider />

                <MDTypography variant="h6" fontWeight="medium" mt={2}>
                  Duties &amp; Taxes
                </MDTypography>
                <Table size="small">
                  <TableBody>
                    {(result.breakdown || []).map((item) => (
                      <SummaryRow
                        key={item.key}
                        label={
                          <>
                            {item.label}
                            {item.rate !== null && item.rate !== undefined ? (
                              <MDTypography variant="caption" color="text">
                                {"  "}
                                {item.rate}% of {item.basis}
                              </MDTypography>
                            ) : null}
                          </>
                        }
                        value={formatMoney(item.amount)}
                      />
                    ))}
                    <SummaryRow label="Total duties & taxes" value={formatMoney(result.totals?.totalDutiesAndTaxesGHS)} bold />
                  </TableBody>
                </Table>

                <MDBox mt={3} p={2} borderRadius="lg" sx={{ backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9" }}>
                  <Table size="small">
                    <TableBody>
                      <SummaryRow label="Total duties & taxes" value={formatMoney(result.totals?.totalDutiesAndTaxesGHS)} bold />
                      {/* <SummaryRow
                        label="Total landed cost (vehicle + charges)"
                        value={formatMoney(result.totals?.totalLandedCostGHS)}
                        bold
                      /> */}
                    </TableBody>
                  </Table>
                </MDBox>
              </MDBox>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </PanelCard>
  );
}

PreviewPanel.propTypes = {
  categoryOptions: PropTypes.array.isRequired,
  onPreview: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default PreviewPanel;
